import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import resourcesToBackend from 'i18next-resources-to-backend';
import { initReactI18next } from 'react-i18next';
import { baseInitOptions } from './baseInitOptions';
import { DEFAULT_LOCALE } from './localeConstants';
import en from './locales/en.json';

// i18next runtime. Mirrors apps/channels/src/localization/i18n.ts, with one
// deliberate difference: only the English catalogue is bundled eagerly; the
// other locales are lazy-loaded as per-locale chunks (a web SPA should not
// ship all catalogues at boot). LocaleProvider drives the active language.
//
// The catalogues rely on the invariants documented in baseInitOptions.ts;
// read that before touching anything here.
// missingKeyHandler fires on every render that hits an unresolved key; report
// each key to Sentry only once per session so a hot component can't flood it.
const reportedMissingKeys = new Set<string>();

void i18n
  .use(resourcesToBackend((language: string) => import(`./locales/${language}.json`)))
  .use(initReactI18next)
  .init({
    ...baseInitOptions,
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    partialBundledLanguages: true,
    // Synchronous init: the default locale's catalogue is bundled, so nothing
    // async is needed and LocaleProvider's readiness gate holds on first render.
    initAsync: false,
    // The catalogues only ship region-tagged files (de-DE.json, no de.json).
    // Without currentOnly, the resolve hierarchy includes base tags and the
    // backend requests files that don't exist, firing failedLoading on every
    // healthy locale switch (pinned by i18n.spec.ts). fallbackLng is
    // unaffected: English stays in the hierarchy.
    load: 'currentOnly',
    resources: { en: { translation: en } },
    // The production regression signal: any key that fails to resolve
    // reports to Sentry.
    saveMissing: true,
    missingKeyHandler: (languages, _namespace, key) => {
      if (reportedMissingKeys.has(key)) return;
      reportedMissingKeys.add(key);
      Sentry.captureMessage(`i18next missing key: ${key}`, {
        level: 'warning',
        tags: { i18nextLanguage: languages.join(',') },
        fingerprint: ['i18next-missing-key', key],
      });
    },
  });

// A locale chunk that fails to load falls back to the bundled English
// catalogue; report it so broken deploys are visible.
i18n.on('failedLoading', (language, _namespace, message) => {
  Sentry.captureMessage(`i18next failed loading locale "${language}": ${message}`, { level: 'warning' });
});

export default i18n;
